import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RpcError, StatusCode } from 'grpc-web';
import { API_URL, getCurrentLng, getHeaders, ApiMessage, toApiMessage, APIRequest, APIResponse, refreshSession, getSession, ACCOUNTANT_API_URL } from '../../../../../../../app/Api';
import { DeleteDocumentRequest, DeleteDocumentResponse, GenerateSecureLinkRequest, GenerateSecureLinkResponse, GetDocumentRequest, GetDocumentResponse } from '../../../../../../../repository/UserManagement/Customers/document_pb';
import { DocumentEndpointClient } from '../../../../../../../repository/UserManagement/Customers/DocumentServiceClientPb';
import { formatDate, timestampToDate } from '../../../../../../../app/Helpers';
import { DynamicField } from '../../../../../../common/DynamicFields/DynamicFields';

export interface DocumentsDetailsPageState {
    item: any
    isChangeStateLoading: boolean,
    message?: ApiMessage,
    isLoading: boolean
    filePreview: string | undefined

}

const initialState: DocumentsDetailsPageState = {
    item: undefined,
    isChangeStateLoading: false,
    message: undefined,
    isLoading: false,
    filePreview: undefined

}

const client = new DocumentEndpointClient(API_URL, null, null);


export const get = createAsyncThunk<GetDocumentResponse.AsObject, APIRequest<GetDocumentRequest>, {

    rejectValue: ApiMessage
}>(
    'pages/customers/documentsDetails/get',
    async (req, thunkApi) => {
        let callReq = async (): Promise<any> => {
            try {
                var r = await client.getDocument(req.body, req.headers ?? {});
                return r.toObject();
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {

                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }
        }
        return await callReq();
    }
)


export const deleteItem = createAsyncThunk<APIResponse<DeleteDocumentResponse.AsObject>, APIRequest<DeleteDocumentRequest>, {

    rejectValue: ApiMessage
}>(
    'pages/customers/documentsDetails/delete',
    async (req, thunkApi) => {
        let callReq = async (): Promise<any> => {

            try {
                var r = await client.delete(req.body, req.headers ?? {});
                return { metadata: { id: req.body.getId() }, response: r.toObject() };
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {

                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }

        }
        return await callReq();
    }
)

export const generateDownloadLink = createAsyncThunk<APIResponse<GenerateSecureLinkResponse.AsObject>, APIRequest<GenerateSecureLinkRequest>, {

    rejectValue: ApiMessage
}>(
    'pages/customers/documentsDetails/generateLink',
    async (req, thunkApi) => {
        let callReq = async (): Promise<any> => {

            try {
                var r = await client.generateSecureLink(req.body, req.headers ?? {});
                return { metadata: { id: req.body.getId() }, response: r.toObject() };
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {

                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }

        }
        return await callReq();
    }
)


export const documentsDetailsPageSlice = createSlice({
    name: 'pages/customers/documentsDetails',
    initialState,
    reducers: {
        dismissMessage: (state) => {
            state.message = undefined;
        },
        reset: (state) => {
            state.message = initialState.message;
            state.isChangeStateLoading = initialState.isChangeStateLoading;
            state.filePreview = initialState.filePreview;

        },

        resetItem: (state) => {
            state.item = undefined;

        },
        setFilePreview: (state, action: PayloadAction<string | undefined>) => {
            state.filePreview = action.payload;
        },
        updateItem: (state, action: PayloadAction<any>) => {
            state.item = action.payload;

        }
    },
    extraReducers: (builder) => {

        builder.addCase(get.fulfilled, (state, { payload }) => {
            state.isLoading = false
            if (payload) {

                let r = {
                    id: payload?.success?.id,
                    name: payload?.success?.name?.value ?? "",
                    createdDate: formatDate(timestampToDate(payload?.success?.createddate?.seconds, payload?.success?.createddate?.nanos)),
                    addedBy: (payload?.success?.createdby?.firstname?.value ?? "") + " " + (payload?.success?.createdby?.lastname?.value ?? ""),
                    addedById: payload?.success?.createdby?.id?.value,
                    extraFields: [] as DynamicField[],
                    ext: payload?.success?.ext?.value ?? "",
                    originalName: payload?.success?.originalname?.value ?? "",
                    ownerFirstName: payload?.success?.owner?.firstname?.value ?? "",
                    ownerLastName: payload?.success?.owner?.lastname?.value ?? "",
                    ownerMiddleName: payload?.success?.owner?.middlename?.value ?? "",
                    ownerName: (payload.success?.owner?.firstname?.value ?? "") + " " + (payload.success?.owner?.lastname?.value ?? ""),
                    ownerId: payload?.success?.owner?.id?.value,
                    size: payload?.success?.size?.value ?? undefined,
                    expirationDate: payload?.success?.expirationdate ? formatDate(timestampToDate(payload?.success?.expirationdate?.seconds, payload?.success?.expirationdate?.nanos), "YYYY-MM-DD") : undefined,

                }

                if (payload?.success?.extrafieldsList) {
                    let count = 0
                    payload?.success?.extrafieldsList?.forEach(element => {
                        r.extraFields.push({ key: element.key, label: element.label, type: element.type, value: element.value })
                        count++
                    });
                }

                state.item = r
            }
        })
        builder.addCase(get.rejected, (state, action) => {
            state.isLoading = false;
            if (action.payload) {
                state.message = action.payload;
            }
        })
        builder.addCase(get.pending, (state, action) => {
            state.isLoading = true;
            state.message = undefined;
        })



        builder.addCase(deleteItem.fulfilled, (state, { payload }) => {
            state.isChangeStateLoading = false;
            if (payload) {
                state.message = toApiMessage(payload?.response.success?.message);
            }
        })
        builder.addCase(deleteItem.rejected, (state, action) => {
            state.isChangeStateLoading = false;
            if (action.payload) {
                state.message = action.payload;
            }
        })
        builder.addCase(deleteItem.pending, (state, action) => {
            state.isChangeStateLoading = true;
            state.message = undefined;
        })

        builder.addCase(generateDownloadLink.fulfilled, (state, { payload }) => {
            if (payload) {
                state.filePreview = payload.response.success?.resourceuri?.value
            }

        })
        builder.addCase(generateDownloadLink.rejected, (state, action) => {
            if (action.payload) {
                state.message = action.payload;
            }
        })
        builder.addCase(generateDownloadLink.pending, (state, action) => {
            state.message = undefined;
        })


    }
})

export const { reset, dismissMessage, resetItem, updateItem, setFilePreview } = documentsDetailsPageSlice.actions

export default documentsDetailsPageSlice.reducer